import React, { useContext, useState } from 'react';
import { Box } from '@mui/material';
import DialogBox from '../Common/Layout/DialogBox';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Backup } from '@mui/icons-material';
import { Colors } from '../../utils/theme';
import {
  IHistoryIndexInput,
  IHistoryStockInput,
  useCreateHistoryIndicesMutation,
  useCreateHistoryStocksMutation
} from '../../generated/graphql';

type IProps = {
  handleClose: () => void;
  isIndexFile: boolean;
};

export type IErrors = {
  fileName: string | null;
};

export const defaultErrorsObj: IErrors = {
  fileName: null
};

export const ErrorTexts = {
  fileName: 'File is required'
};

const CLEAR_TOKEN = 'NULL';

const indexNumericFields = [
  'mkt_cap_eur',
  'mkt_cap_usd',
  'mkt_cap_gbp',
  'mkt_cap_jpy',
  'mkt_cap_aud',
  'price_close_eur',
  'price_close_usd',
  'price_close_gbp',
  'price_close_jpy',
  'price_close_aud',
  'price_return_eur',
  'price_return_usd',
  'price_return_gbp',
  'price_return_jpy',
  'price_return_aud',
  'price_net_eur',
  'price_net_usd',
  'price_net_gbp',
  'price_net_jpy',
  'price_net_aud'
] as const;

const stockNumericFields = [
  'close_price',
  'engl_mkt_cap',
  'engl_mkt_cap_after_inv_wght',
  'gross_div_yield',
  'div_net',
  'epra_shares'
] as const;

const indexAllowedHeaders = new Set<string>(['index_code', 'date_day', ...indexNumericFields]);
const stockAllowedHeaders = new Set<string>([
  'unique_stock_code',
  'date_day',
  ...stockNumericFields
]);

const splitCsvRows = (content: string) =>
  content
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((row) => row.trim() !== '');

const splitCsvCells = (row: string) => row.split(',').map((cell) => cell.trim());

const parseNumericCell = (raw: string): number | null | undefined => {
  if (raw === '') {
    return undefined;
  }
  if (raw.toUpperCase() === CLEAR_TOKEN) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const HistoryUpload: React.FunctionComponent<IProps> = ({ handleClose, isIndexFile }: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [fileName, setFileName] = useState('');
  const [fileHistoryStocks, setFileHistoryStocks] = useState<IHistoryStockInput[]>([]);
  const [fileHistoryIndieces, setFileHistoryIndices] = useState<IHistoryIndexInput[]>([]);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [uploadHistoryStocks] = useCreateHistoryStocksMutation();
  const [uploadHistoryIndices] = useCreateHistoryIndicesMutation();

  const showMessage = (severity: 'success' | 'error' | 'info', text: string, autoHide = 4000) => {
    dispatch({
      type: Actions.ShowMessage,
      payload: {
        severity,
        text,
        ...(severity === 'error' ? { autoHide: 6000 } : autoHide !== 4000 ? { autoHide } : {})
      }
    });
  };

  const importHistoryStocks = () => {
    const errors = {
      fileName: null
    } as IErrors;
    let hasErrors = false;
    if (!fileName) {
      hasErrors = true;
      errors.fileName = ErrorTexts.fileName;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      if (isIndexFile) {
        uploadHistoryIndices({
          variables: {
            input: fileHistoryIndieces
          },
          update(_, { data }) {
            if (data?.result) {
              showMessage('success', `Successfully imported data`);
              handleClose();
            }
          }
        }).catch((e) => {
          showMessage('error', e.message);
        });
      } else {
        uploadHistoryStocks({
          variables: {
            input: fileHistoryStocks
          },
          update(_, { data }) {
            if (data?.result) {
              showMessage('success', `Successfully imported data`);
              handleClose();
            }
          }
        }).catch((e) => {
          showMessage('error', e.message);
        });
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target && event.target.files) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = (e) => {
          if (e && e.target && e.target.result) {
            const rows = splitCsvRows(e.target.result as string);
            if (rows.length < 2) {
              showMessage('info', 'no data to upload');
              return;
            }

            const headers = splitCsvCells(rows[0]);
            const dataRows = rows.slice(1);

            if (isIndexFile) {
              if (!headers.includes('index_code') || !headers.includes('date_day')) {
                showMessage('error', 'CSV must include index_code and date_day columns');
                return;
              }

              const historyIndices = dataRows.map((row) => {
                const values = splitCsvCells(row);
                const record: IHistoryIndexInput = {
                  index_code: '',
                  date_day: ''
                };

                headers.forEach((header, i) => {
                  if (!indexAllowedHeaders.has(header)) {
                    return;
                  }
                  const raw = values[i] ?? '';
                  if (header === 'index_code' || header === 'date_day') {
                    record[header] = raw;
                    return;
                  }
                  const parsed = parseNumericCell(raw);
                  if (parsed !== undefined) {
                    (record as Record<string, number | null | string>)[header] = parsed;
                  }
                });

                return record;
              });

              setFileName(file.name);
              setFileHistoryIndices(historyIndices);
            } else {
              if (!headers.includes('unique_stock_code') || !headers.includes('date_day')) {
                showMessage('error', 'CSV must include unique_stock_code and date_day columns');
                return;
              }

              const historyStocks = dataRows.map((row) => {
                const values = splitCsvCells(row);
                const record: IHistoryStockInput = {
                  unique_stock_code: '',
                  date_day: ''
                };

                headers.forEach((header, i) => {
                  if (!stockAllowedHeaders.has(header)) {
                    return;
                  }
                  const raw = values[i] ?? '';
                  if (header === 'unique_stock_code' || header === 'date_day') {
                    record[header] = raw;
                    return;
                  }
                  const parsed = parseNumericCell(raw);
                  if (parsed !== undefined) {
                    (record as Record<string, number | null | string>)[header] = parsed;
                  }
                });

                return record;
              });

              setFileName(file.name);
              setFileHistoryStocks(historyStocks);
            }
          }
        };
      } else {
        setFormErrors({
          ...formErrors,
          fileName: ErrorTexts.fileName
        });
      }
    }
    event.target.value = '';
  };

  return (
    <DialogBox
      id="history-upload-form-dialog"
      width={380}
      title={isIndexFile ? 'Import Index Data' : 'Import Stock Data'}
      onClose={handleClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', m: '30px', gap: '10px' }}>
        <InputComponent
          id="history-upload-file"
          label=""
          value={fileName}
          onChange={handleFileUpload}
          errorMsg={formErrors.fileName}
          disabled
          endAdornment={
            <Box sx={{ position: 'relative', bottom: '3px' }}>
              <input
                accept="text/csv"
                id="event-file"
                type="file"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                multiple={true}
              />
              <label htmlFor="event-file">
                <Backup
                  style={{
                    cursor: 'pointer',
                    paddingRight: '5%',
                    paddingTop: '5px',
                    color: Colors.main
                  }}
                />
              </label>
            </Box>
          }
        />
        <Box className="centralize" sx={{ gridColumn: 'span 3', marginBottom: '15px', mt: '20px' }}>
          <CancelButton id="cancel-history-upload-button" onClick={handleClose} text="Cancel" />
          <SaveButton
            id="add-history-data-button"
            onClick={() => importHistoryStocks()}
            text="Import"
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default HistoryUpload;
